import {
  ArrowsPointingInIcon,
  ArrowUpTrayIcon,
  BoltIcon,
  DocumentArrowUpIcon,
  DocumentChartBarIcon,
  QueueListIcon,
} from "@heroicons/react/16/solid";
import {ComponentType, SVGProps} from "react";
import {ProcessKind, processKindInfo} from "./process-ui";

const icons: Record<ProcessKind, ComponentType<SVGProps<SVGSVGElement>>> = {
  report: DocumentChartBarIcon,
  merge: ArrowsPointingInIcon,
  dataAction: BoltIcon,
  export: ArrowUpTrayIcon,
  import: DocumentArrowUpIcon,
  other: QueueListIcon,
};

/** Tinted per group so reports, record changes and transfers read apart at a glance. */
const tones: Record<ProcessKind, string> = {
  report: "bg-[var(--ots-primary-container)] text-[var(--ots-on-primary-container)]",
  merge: "bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]",
  dataAction: "bg-[var(--ots-indigo-container)] text-[var(--ots-on-indigo-container)]",
  export: "bg-[var(--ots-neutral-container)] text-[var(--ots-text-muted)]",
  import: "bg-[var(--ots-neutral-container)] text-[var(--ots-text-muted)]",
  other: "bg-[var(--ots-neutral-container)] text-[var(--ots-text-muted)]",
};

export type ProcessKindIconProps = {
  kind: ProcessKind;
  /** "tile" sits beside a row's title; "inline" is the bare glyph, e.g. in a filter chip. */
  variant?: "tile" | "inline";
  /** Hides it from screen readers when the text beside it already says the kind. */
  decorative?: boolean;
  className?: string;
};

/** What sort of process this is, as an icon labelled for screen readers ("Report", "Merge", …). */
export const ProcessKindIcon = ({kind, variant = "tile", decorative = false, className}: ProcessKindIconProps) => {
  const Icon = icons[kind] ?? icons.other;
  const label = processKindInfo(kind).label;

  const a11y = decorative ? {"aria-hidden": true} : {role: "img", "aria-label": label};

  if (variant === "inline")
    // heroicons hide themselves unless told otherwise
    return <Icon {...a11y} aria-hidden={decorative} data-kind={kind} className={["size-3.5 shrink-0", className].filter(Boolean).join(" ")}/>;

  return <span
    {...a11y}
    title={decorative ? undefined : label}
    data-kind={kind}
    className={["inline-flex size-6 shrink-0 items-center justify-center rounded-md", tones[kind] ?? tones.other, className].filter(Boolean).join(" ")}>
    <Icon aria-hidden="true" className="size-3.5"/>
  </span>;
};
